import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.jsx';
import { useTeams } from '@/features/teams/api/use-teams.js';
import { TeamAvatar } from '@/features/teams/components/team-avatar.jsx';
import { useTeamScope } from '@/providers/team-scope-provider.jsx';

const PERSONAL_VALUE = '__personal__';

/**
 * Global team scope picker rendered in the topbar. Drives every
 * team-aware page (dashboard, cloud, repositories) via TeamScopeProvider.
 *
 * Empty teamUuid in context = personal team. Any non-empty value is an
 * org team UUID; the backend enforces membership/admin permission on writes.
 */
export function TeamScopeSelect() {
  const { teamUuid, setTeamUuid } = useTeamScope();
  const { data, isPending, isError } = useTeams();

  const personalTeam = data?.teams?.find((t) => !t.github_org);
  const orgTeams = (data?.teams ?? []).filter((t) => t.github_org);

  const selectValue = teamUuid === '' ? PERSONAL_VALUE : teamUuid;

  const handleChange = (next) => {
    setTeamUuid(next === PERSONAL_VALUE ? '' : next);
  };

  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={isPending || isError}>
      <SelectTrigger size="sm" className="w-48" aria-label="Team scope">
        <SelectValue placeholder="Personal" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={PERSONAL_VALUE}>
          <span className="flex items-center gap-2">
            <TeamAvatar team={personalTeam ?? { github_org: '' }} />
            {personalTeam?.name ?? 'Personal'}
          </span>
        </SelectItem>
        {orgTeams.map((team) => (
          <SelectItem key={team.uuid} value={team.uuid}>
            <span className="flex items-center gap-2">
              <TeamAvatar team={team} />
              {team.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
